import { Skeleton } from "../ui/skeleton";
import { motion } from "framer-motion";

export const PartyHealthCheckSkeleton = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Progress Indicator */}
      <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-2xl p-5 border border-cyan-500/30">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-10 h-10 rounded-full border-4 border-cyan-500/30 border-t-cyan-500 animate-spin"></div>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-white mb-1">Verifying Party Status</h3>
            <p className="text-xs text-slate-400">Checking ECI database and Form 24A records...</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mt-4 text-xs">
          <span className="flex items-center gap-1.5 text-cyan-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Party Found
          </span>
          <div className="w-8 h-px bg-slate-600"></div>
          <span className="flex items-center gap-1.5 text-cyan-400/50 animate-pulse">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"></div>
            Fetching Data
          </span>
          <div className="w-8 h-px bg-slate-700"></div>
          <span className="flex items-center gap-1.5 text-slate-500">
            <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-600"></div>
            Generating Report
          </span>
        </div>
      </div>

      {/* Party Header Skeleton */}
      <div className="rounded-2xl p-6 border bg-slate-800/50 border-slate-700">
        <div className="flex items-center gap-4">
          {/* Logo Skeleton */}
          <Skeleton className="w-20 h-20 rounded-xl bg-slate-700/50" />
          
          {/* Info Skeleton */}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48 bg-slate-600/50" />
            <Skeleton className="h-4 w-24 bg-slate-700/50" />
            <Skeleton className="h-5 w-32 bg-slate-600/50" />
            <Skeleton className="h-5 w-40 bg-slate-700/50" />
          </div>
        </div>
      </div>

      {/* Data Matrix Skeleton */}
      <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="w-5 h-5 rounded bg-cyan-500/30" />
          <Skeleton className="h-5 w-28 bg-slate-600/50" />
        </div>
        
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex items-center border-b border-slate-700/50 pb-3">
              <Skeleton className="h-4 w-32 bg-slate-700/50" />
              <div className="flex-1 ml-4">
                <Skeleton className="h-4 w-48 bg-slate-600/50" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Evidence Section Skeleton */}
      <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="w-5 h-5 rounded bg-emerald-500/30" />
          <Skeleton className="h-5 w-36 bg-slate-600/50" />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-slate-900/50 rounded-xl p-4 text-center">
              <Skeleton className="h-3 w-20 mx-auto mb-2 bg-slate-700/50" />
              <Skeleton className="h-5 w-16 mx-auto bg-slate-600/50" />
            </div>
          ))}
        </div>
      </div>

      {/* Form 24A History Skeleton */}
      <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="w-5 h-5 rounded bg-amber-500/30" />
          <Skeleton className="h-5 w-52 bg-slate-600/50" />
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="py-3 px-4 text-left"><Skeleton className="h-4 w-24 bg-slate-600/50" /></th>
                <th className="py-3 px-4 text-left"><Skeleton className="h-4 w-20 bg-slate-600/50" /></th>
                <th className="py-3 px-4 text-left"><Skeleton className="h-4 w-16 bg-slate-600/50" /></th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className="border-b border-slate-700/50">
                  <td className="py-3 px-4"><Skeleton className="h-4 w-20 bg-slate-700/50" /></td>
                  <td className="py-3 px-4"><Skeleton className="h-6 w-16 rounded-full bg-slate-600/50" /></td>
                  <td className="py-3 px-4"><Skeleton className="h-8 w-24 rounded-lg bg-slate-700/50" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loading Tip */}
      <div className="text-center py-2">
        <p className="text-slate-500 text-xs animate-pulse">
          🔍 Cross-referencing with Election Commission of India records...
        </p>
      </div>
    </motion.div>
  );
};

export default PartyHealthCheckSkeleton;
